<template>
  <div class="reports-layout">

    <!-- ============================================================ Left sidebar: catalog -->
    <div class="reports-sidebar">
      <div class="sidebar-header">
        <i class="mdi mdi-file-chart-outline sidebar-icon" />
        <span>Report Catalog</span>
      </div>

      <div v-if="catalogLoading" class="sidebar-loading">
        <PProgressSpinner style="width:24px;height:24px;" />
      </div>

      <ul v-else class="catalog-list">
        <li
          v-for="item in catalog"
          :key="item.type"
          class="catalog-item"
          :class="{ active: selected && selected.type === item.type }"
          @click="selectReport(item)"
        >
          <i class="mdi catalog-item-icon" :class="reportIcon(item.type)" />
          <div class="catalog-item-text">
            <div class="catalog-item-label">{{ item.label }}</div>
            <div class="catalog-item-deliverable">{{ item.deliverable }}</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- ============================================================ Main panel -->
    <div class="reports-main">

      <!-- No selection -->
      <div v-if="!selected" class="reports-empty">
        <i class="mdi mdi-arrow-left-circle-outline reports-empty-icon" />
        <p>Select a report from the catalog to get started.</p>
      </div>

      <template v-else>

        <!-- Report header -->
        <div class="report-panel-header">
          <div>
            <h2 class="report-panel-title">{{ selected.label }}</h2>
            <span class="report-panel-deliverable">{{ selected.deliverable }} · REQ-RPT</span>
          </div>
          <div class="report-panel-actions">
            <PButton
              label="Generate"
              icon="mdi mdi-play"
              :loading="generating"
              :disabled="!canGenerate"
              @click="generate"
              severity="success"
              size="small"
            />
          </div>
        </div>

        <!-- Filters -->
        <PCard class="filters-card">
          <template #title>
            <span style="font-size:13px;font-weight:600;">
              <i class="mdi mdi-filter-outline mr-2" />Filters
            </span>
          </template>
          <template #content>
            <div class="filters-grid">

              <!-- Billing period (most reports) -->
              <div class="filter-field">
                <label>Billing Period</label>
                <PDropdown
                  v-model="filters.billing_period_id"
                  :options="billingPeriods"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select period…"
                  showClear
                  class="w-full"
                />
              </div>

              <!-- Date from / to (optional overrides) -->
              <div class="filter-field">
                <label>Date From</label>
                <PInputText v-model="filters.date_from" type="date" class="w-full" />
              </div>
              <div class="filter-field">
                <label>Date To</label>
                <PInputText v-model="filters.date_to" type="date" class="w-full" />
              </div>

              <!-- Sort -->
              <div class="filter-field">
                <label>Sort Order</label>
                <PSelectButton
                  v-model="filters.sort_order"
                  :options="sortOrderOptions"
                  optionLabel="label"
                  optionValue="value"
                />
              </div>

              <!-- Format -->
              <div class="filter-field">
                <label>Output Format</label>
                <PSelectButton
                  v-model="filters.format"
                  :options="formatOptions"
                  optionLabel="label"
                  optionValue="value"
                />
              </div>

              <!-- Audit sub-type (only for audit report) -->
              <div v-if="selected.type === 'audit'" class="filter-field">
                <label>Audit Sub-Report</label>
                <PDropdown
                  v-model="filters.audit_sub_type"
                  :options="auditSubTypes"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
              </div>

            </div>
          </template>
        </PCard>

        <!-- Result panel -->
        <div v-if="lastResult" class="result-panel">
          <div class="result-header">
            <i class="mdi mdi-check-circle result-icon" />
            <div>
              <div class="result-title">{{ lastResult.title }}</div>
              <div class="result-subtitle">
                {{ lastResult.subtitle }} · {{ lastResult.row_count.toLocaleString() }} rows
              </div>
            </div>
          </div>

          <!-- Summary chips -->
          <div v-if="Object.keys(lastResult.summary || {}).length" class="summary-chips">
            <div v-for="(v, k) in lastResult.summary" :key="k" class="summary-chip">
              <span class="chip-key">{{ formatKey(k) }}</span>
              <span class="chip-val">{{ v ?? '—' }}</span>
            </div>
          </div>

          <!-- Download buttons -->
          <div class="download-buttons">
            <PButton
              v-if="lastResult.pdf_file"
              label="Download PDF"
              icon="mdi mdi-file-pdf-box"
              severity="danger"
              size="small"
              @click="downloadFile(lastResult.pdf_file)"
            />
            <PButton
              v-if="lastResult.xlsx_file"
              label="Download XLSX"
              icon="mdi mdi-microsoft-excel"
              severity="success"
              size="small"
              @click="downloadFile(lastResult.xlsx_file)"
            />
            <span v-if="!lastResult.pdf_file && !lastResult.xlsx_file" class="no-files-note">
              No output files were generated (check server logs).
            </span>
          </div>
        </div>

        <!-- Error panel -->
        <div v-if="errorMsg" class="error-panel">
          <i class="mdi mdi-alert-circle-outline" />
          <span>{{ errorMsg }}</span>
        </div>

      </template>

      <!-- ============================================================ Generated files list -->
      <PCard v-if="showFilesList" class="files-card mt-4">
        <template #title>
          <div class="flex align-items-center justify-content-between">
            <span style="font-size:13px;font-weight:600;">
              <i class="mdi mdi-folder-outline mr-2" />Generated Files
            </span>
            <PButton
              label="Refresh"
              icon="mdi mdi-refresh"
              size="small"
              severity="secondary"
              text
              @click="loadFilesList"
            />
          </div>
        </template>
        <template #content>
          <div v-if="filesLoading" class="text-center py-3">
            <PProgressSpinner style="width:24px;height:24px;" />
          </div>
          <PDataTable
            v-else
            :value="generatedFiles"
            :rows="15"
            :rowsPerPageOptions="[15, 30, 100]"
            paginator
            size="small"
            :alwaysShowPaginator="false"
            stripedRows
            :showGridlines="true"
          >
            <template #empty>No report files found.</template>
            <PColumn field="filename" header="Filename" />
            <PColumn field="size_kb" header="Size (KB)">
              <template #body="{ data }">{{ data.size_kb }} KB</template>
            </PColumn>
            <PColumn header="Download">
              <template #body="{ data }">
                <PButton
                  :label="data.filename.endsWith('.pdf') ? 'PDF' : 'XLSX'"
                  :icon="data.filename.endsWith('.pdf') ? 'mdi mdi-file-pdf-box' : 'mdi mdi-microsoft-excel'"
                  :severity="data.filename.endsWith('.pdf') ? 'danger' : 'success'"
                  size="small"
                  text
                  @click="downloadFile(data.filename)"
                />
              </template>
            </PColumn>
          </PDataTable>
        </template>
      </PCard>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ReportsBroker, { ReportFilter, GenerateReportResult } from '@/brokers/ReportsBroker'
import Constants from '@/helpers/Constants'

interface CatalogItem { type: string; label: string; deliverable: string }
interface BillingPeriod { id: number; name: string; status: string }
interface GeneratedFile { filename: string; size_kb: number; created_at: number }

export default defineComponent({
  name: 'ReportsView',

  data() {
    return {
      // Catalog
      catalog: [] as CatalogItem[],
      catalogLoading: true,

      // Selection
      selected: null as CatalogItem | null,

      // Filters
      filters: {
        billing_period_id: null as number | null,
        date_from: null as string | null,
        date_to: null as string | null,
        sort_order: 'asc' as 'asc' | 'desc',
        format: 'both' as 'pdf' | 'xlsx' | 'both',
        audit_sub_type: 'adjustments',
      },

      // Reference data
      billingPeriods: [] as BillingPeriod[],

      // Generation state
      generating: false,
      lastResult: null as GenerateReportResult | null,
      errorMsg: null as string | null,

      // Files list
      showFilesList: false,
      generatedFiles: [] as GeneratedFile[],
      filesLoading: false,

      // Options
      formatOptions: [
        { label: 'PDF + XLSX', value: 'both' },
        { label: 'PDF only', value: 'pdf' },
        { label: 'XLSX only', value: 'xlsx' },
      ],
      sortOrderOptions: [
        { label: '↑ Ascending', value: 'asc' },
        { label: '↓ Descending', value: 'desc' },
      ],
      auditSubTypes: [
        { label: 'Adjustments History', value: 'adjustments' },
        { label: 'Manual Entries',       value: 'manual_entries' },
        { label: 'Raw Data Inserts',     value: 'raw_inserts' },
      ],
    }
  },

  computed: {
    canGenerate(): boolean {
      // Most reports require at minimum a billing period OR date range
      const { billing_period_id, date_from, date_to } = this.filters
      return !!(billing_period_id || date_from || date_to)
    },
  },

  async mounted() {
    await Promise.all([this.loadCatalog(), this.loadBillingPeriods()])
    await this.loadFilesList()
    this.showFilesList = true
  },

  methods: {
    async loadCatalog() {
      this.catalogLoading = true
      try {
        const res = await ReportsBroker.getCatalog()
        this.catalog = Array.isArray(res) ? res : []
      } catch (e) {
        this.catalog = []
      } finally {
        this.catalogLoading = false
      }
    },

    async loadBillingPeriods() {
      try {
        const res = await ReportsBroker.getBillingPeriods()
        this.billingPeriods = Array.isArray(res) ? res : []
      } catch {
        this.billingPeriods = []
      }
    },

    async loadFilesList() {
      this.filesLoading = true
      try {
        const res = await ReportsBroker.listReports()
        this.generatedFiles = Array.isArray(res) ? res : []
      } catch {
        this.generatedFiles = []
      } finally {
        this.filesLoading = false
      }
    },

    selectReport(item: CatalogItem) {
      this.selected = item
      this.lastResult = null
      this.errorMsg = null
    },

    async generate() {
      if (!this.selected) return
      this.generating = true
      this.lastResult = null
      this.errorMsg = null

      const payload: ReportFilter = {
        report_type:       this.selected.type,
        billing_period_id: this.filters.billing_period_id,
        date_from:         this.filters.date_from || null,
        date_to:           this.filters.date_to || null,
        sort_order:        this.filters.sort_order,
        format:            this.filters.format,
        audit_sub_type:    this.filters.audit_sub_type,
      }

      try {
        const res = await ReportsBroker.generateReport(payload)
        this.lastResult = res
        // Refresh the files list after successful generation
        await this.loadFilesList()
      } catch (e: any) {
        // AxiosManager already toasts the server message; mirror it in the panel.
        this.errorMsg = e?.message || 'Unexpected error during report generation.'
      } finally {
        this.generating = false
      }
    },

    downloadFile(filename: string) {
      window.open(ReportsBroker.downloadUrl(filename), '_blank')
    },

    reportIcon(type: string): string {
      const icons: Record<string, string> = {
        invoice_package:    'mdi-receipt',
        metering_deviation: 'mdi-compare-horizontal',
        availability:       'mdi-calendar-check-outline',
        power_export_import:'mdi-transmission-tower',
        nee_export:         'mdi-lightning-bolt-outline',
        payment:            'mdi-currency-usd',
        raw_metering_data:  'mdi-database-outline',
        data_validation:    'mdi-shield-check-outline',
        ppm:                'mdi-chart-line',
        audit:              'mdi-history',
        manual_contractual: 'mdi-pencil-box-outline',
        contract_values:    'mdi-file-document-outline',
      }
      return icons[type] || 'mdi-file-outline'
    },

    formatKey(k: string): string {
      return k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace('Kwh', 'kWh')
    },
  },
})
</script>

<style scoped>
/* ----------------------------------------------------------------- Layout */
.reports-layout {
  display: flex;
  gap: 0;
  height: calc(100vh - 80px);
  overflow: hidden;
}

/* ----------------------------------------------------------------- Sidebar */
.reports-sidebar {
  width: 260px;
  min-width: 220px;
  background: var(--surface-card, #fff);
  border-right: 1px solid var(--surface-border, #e0e0e0);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-shrink: 0;
}
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 10px;
  font-weight: 700;
  font-size: 13px;
  color: var(--primary-color, #1F4E79);
  border-bottom: 1px solid var(--surface-border, #e0e0e0);
}
.sidebar-icon { font-size: 18px; }
.sidebar-loading { display: flex; justify-content: center; padding: 24px; }
.catalog-list { list-style: none; padding: 6px 0; margin: 0; }
.catalog-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  border-radius: 6px;
  margin: 2px 6px;
  transition: background 0.15s;
}
.catalog-item:hover { background: var(--surface-hover, #f4f7fb); }
.catalog-item.active {
  background: var(--primary-50, #e3ecf7);
  color: var(--primary-color, #1F4E79);
  font-weight: 600;
}
.catalog-item-icon { font-size: 17px; color: var(--primary-color, #1F4E79); }
.catalog-item-label { font-size: 12px; line-height: 1.3; }
.catalog-item-deliverable { font-size: 10px; color: var(--text-color-secondary, #888); margin-top: 1px; }

/* ----------------------------------------------------------------- Main */
.reports-main {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.reports-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-color-secondary, #999);
  font-size: 14px;
  gap: 12px;
}
.reports-empty-icon { font-size: 48px; opacity: 0.35; }

/* ----------------------------------------------------------------- Panel header */
.report-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--primary-color, #1F4E79);
}
.report-panel-title { font-size: 16px; font-weight: 700; color: var(--primary-color, #1F4E79); margin: 0; }
.report-panel-deliverable { font-size: 11px; color: var(--text-color-secondary, #888); }

/* ----------------------------------------------------------------- Filters */
.filters-card { flex-shrink: 0; }
.filters-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}
.filter-field label { font-size: 11px; font-weight: 600; color: var(--text-color-secondary, #555); }

/* ----------------------------------------------------------------- Result */
.result-panel {
  background: var(--surface-card, #fff);
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.result-header { display: flex; align-items: center; gap: 10px; }
.result-icon { font-size: 22px; color: #4caf50; }
.result-title { font-size: 13px; font-weight: 700; }
.result-subtitle { font-size: 11px; color: var(--text-color-secondary, #888); }

/* Summary chips */
.summary-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.summary-chip {
  background: var(--surface-ground, #f5f7fa);
  border: 1px solid var(--surface-border, #ddd);
  border-radius: 16px;
  padding: 4px 12px;
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 11px;
}
.chip-key { color: var(--text-color-secondary, #666); }
.chip-val { font-weight: 700; color: var(--primary-color, #1F4E79); }

.download-buttons { display: flex; gap: 8px; flex-wrap: wrap; }
.no-files-note { font-size: 11px; color: var(--text-color-secondary, #888); align-self: center; }

/* ----------------------------------------------------------------- Error */
.error-panel {
  background: #fff3f3;
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #c62828;
}

/* ----------------------------------------------------------------- Files card */
.files-card { flex-shrink: 0; }
</style>
